import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Crown, Users, UserPlus, TrendingUp } from 'lucide-react';
import api from '../utils/api';

const ReferralLeaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [memberStats, setMemberStats] = useState({ total_members: 0, active_members: 0, new_today: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leaderboardRes, statsRes] = await Promise.all([
          api.get('/referral/leaderboard'),
          api.get('/stats/members')
        ]);
        setLeaderboard(leaderboardRes.data);
        setMemberStats(statsRes.data);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-300" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-sm font-bold text-muted-foreground">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-500/30';
      case 2:
        return 'bg-gradient-to-r from-gray-400/20 to-gray-300/20 border-gray-400/30';
      case 3:
        return 'bg-gradient-to-r from-amber-700/20 to-amber-600/20 border-amber-700/30';
      default:
        return 'bg-white/5 border-white/10';
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-6 animate-pulse">
        <div className="h-8 bg-white/10 rounded w-1/2 mb-4"></div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-white/5 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Member Stats Cards */}
      <div className="grid grid-cols-3 gap-4" data-testid="member-stats">
        <div className="glass-card p-4 text-center">
          <div className="flex justify-center mb-2">
            <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
          </div>
          <p className="text-2xl font-bold text-primary" data-testid="total-members">{memberStats.total_members}</p>
          <p className="text-xs text-muted-foreground">Total Members</p>
        </div>
        
        <div className="glass-card p-4 text-center">
          <div className="flex justify-center mb-2">
            <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-green-500" data-testid="active-members">{memberStats.active_members}</p>
          <p className="text-xs text-muted-foreground">Active Members</p>
        </div>
        
        <div className="glass-card p-4 text-center">
          <div className="flex justify-center mb-2">
            <div className="w-10 h-10 bg-accent/20 rounded-xl flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-accent" />
            </div>
          </div>
          <p className="text-2xl font-bold text-accent" data-testid="new-today">{memberStats.new_today}</p>
          <p className="text-xs text-muted-foreground">New Today</p>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="glass-card p-6" data-testid="referral-leaderboard">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Referral Leaderboard</h3>
            <p className="text-sm text-muted-foreground">Top performers this month</p>
          </div>
        </div>

        {leaderboard.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No referrals yet. Be the first!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((entry, idx) => (
              <div
                key={entry.username}
                className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:scale-[1.02] ${getRankBg(entry.rank)}`}
                data-testid={`leaderboard-entry-${entry.rank}`}
              >
                <div className="flex-shrink-0">
                  {getRankIcon(entry.rank)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{entry.name}</p>
                  <p className="text-sm text-muted-foreground">@{entry.username}</p>
                </div>
                
                <div className="text-right">
                  <p className="text-xl font-bold text-primary">{entry.referrals}</p>
                  <p className="text-xs text-muted-foreground">referrals</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralLeaderboard;
